import React from 'react';
import './MarketplaceObjectMenu.css';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared Marketplace Object Menu Component
 * Used in LandingPage (marketplace tab) and MeinMarktplatz for consistent 3-dot menu design
 *
 * @param {boolean} isOpen - Whether the menu is open
 * @param {Function} onClose - Close the menu
 * @param {Function} onEdit - Edit callback (only shown if isOwner)
 * @param {Function} onDelete - Delete callback (only shown if isOwner)
 * @param {Function} onMarkAsSold - Mark as sold/found callback (only shown if showMarkAsSold is true)
 * @param {Function} onShare - Share callback (Issue #312: shown for all users)
 * @param {Function} onReport - Report callback (only shown if not isOwner)
 * @param {boolean} isOwner - Whether current user owns the object
 * @param {boolean} showMarkAsSold - Whether to show "Mark as sold/found" option (for "Meine Objekte" tab)
 * @param {string} marketplaceType - 'offer' or 'search' to determine button text (Issue #329)
 */
const MarketplaceObjectMenu = ({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onMarkAsSold,
  onShare,
  onReport,
  isOwner,
  showMarkAsSold = false,
  marketplaceType = 'offer'
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleKeyDown = (e, action, isLast) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.nextElementSibling;
      if (next?.getAttribute('role') === 'menuitem') next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = e.currentTarget.previousElementSibling;
      if (prev?.getAttribute('role') === 'menuitem') prev.focus();
    }
  };

  return (
    <>
      {/* Overlay for click-away functionality */}
      <div
        className="marketplace-menu-overlay"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      ></div>
      <div className="marketplace-menu-dropdown" role="menu" aria-label={t('marketplace.options')}>
        {isOwner ? (
          <>
            <button
              role="menuitem"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              onKeyDown={(e) => handleKeyDown(e, 'edit')}
            >
              {t('edit')}
            </button>
            {/* Issue #312: Share object button */}
            {onShare && (
              <button
                role="menuitem"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onShare();
                }}
                onKeyDown={(e) => handleKeyDown(e, 'share')}
              >
                {t('share.shareObject')}
              </button>
            )}
            <button
              role="menuitem"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              onKeyDown={(e) => handleKeyDown(e, 'delete')}
              className="delete-option"
            >
              {t('delete')}
            </button>
            {/* Issue #329: Show "Ich habe es gefunden" for search objects, "Als verkauft markieren" for offers */}
            {showMarkAsSold && (
              <button
                role="menuitem"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsSold();
                }}
                onKeyDown={(e) => handleKeyDown(e, 'markAsSold')}
              >
                {marketplaceType === 'search' ? t('marketplace.markAsFound') : t('marketplace.markAsSold')}
              </button>
            )}
          </>
        ) : (
          <>
            {/* Issue #312: Share object button */}
            {onShare && (
              <button
                role="menuitem"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onShare();
                }}
                onKeyDown={(e) => handleKeyDown(e, 'share')}
              >
                {t('share.shareObject')}
              </button>
            )}
            <button
              role="menuitem"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onReport();
              }}
              onKeyDown={(e) => handleKeyDown(e, 'report')}
            >
              {t('marketplace.reportObject')}
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default MarketplaceObjectMenu;
